import React, { FormEvent, useCallback, useEffect } from 'react';
import {
  Calendar,
  FileText,
  MessageSquare,
  Phone,
  Play,
  Send,
  Users
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  HsmContact,
  HsmContactBatch,
  HsmCampaignMessage,
  HsmMessageLog,
  HsmCampaignStats,
  HsmContactFilter,
  HsmContactSortKey,
  HsmTemplate,
  HsmTemplatesResponse,
  HsmActiveTab,
  HsmTestMessageRequest,
  Campaign,
  CampaignDetail,
  CampaignCreateRequest,
  CampaignAddContactsRequest
} from '../../types';
import { CampaignRestartRequest } from '../../services/hsm-campaigns.service';
import type { TemplateVariable } from '../../services/hsm.service';
import {
  HsmContactsTab,
  HsmCampaignsTab,
  HsmMessagesTab,
  HsmTemplatesTab,
  HsmLogsTab,
  HsmTestMessageDialog
} from '../hsm';

interface HsmPageProps {
  // Active tab
  activeTab: HsmActiveTab;
  setActiveTab: (tab: HsmActiveTab) => void;

  // Contacts state
  contacts: HsmContact[];
  contactsLoading: boolean;
  contactsFilter: HsmContactFilter;
  setContactsFilter: (filter: HsmContactFilter) => void;
  contactsSearch: string;
  setContactsSearch: (search: string) => void;
  contactsSort: { column: HsmContactSortKey; direction: 'asc' | 'desc' };
  toggleContactsSort: (column: HsmContactSortKey) => void;
  contactDialogOpen: boolean;
  setContactDialogOpen: (open: boolean) => void;
  bulkImportDialogOpen: boolean;
  setBulkImportDialogOpen: (open: boolean) => void;
  selectedContact: HsmContact | null;

  // Contact form state
  contactPhoneNumber: string;
  setContactPhoneNumber: (value: string) => void;
  contactName: string;
  setContactName: (value: string) => void;
  contactCompany: string;
  setContactCompany: (value: string) => void;
  contactCpfCnpj: string;
  setContactCpfCnpj: (value: string) => void;
  contactEmail: string;
  setContactEmail: (value: string) => void;
  contactCampaignDay: string;
  setContactCampaignDay: (value: string) => void;
  contactBatchId: string;
  setContactBatchId: (value: string) => void;
  contactSaving: boolean;

  // Batch state
  batches: HsmContactBatch[];
  batchesLoading: boolean;
  selectedBatchFilter: number | null;
  setSelectedBatchFilter: (batchId: number | null) => void;
  batchDialogOpen: boolean;
  setBatchDialogOpen: (open: boolean) => void;
  batchName: string;
  setBatchName: (value: string) => void;
  batchDescription: string;
  setBatchDescription: (value: string) => void;
  batchSaving: boolean;
  assignBatchDialogOpen: boolean;
  setAssignBatchDialogOpen: (open: boolean) => void;
  contactToAssign: HsmContact | null;
  assignBatchId: string;
  setAssignBatchId: (value: string) => void;
  assigningBatch: boolean;

  // Batch edit state
  batchEditDialogOpen: boolean;
  setBatchEditDialogOpen: (open: boolean) => void;
  batchToEdit: HsmContactBatch | null;

  // Campaigns state (new system)
  campaigns: Campaign[];
  campaignsLoading: boolean;
  selectedCampaign: CampaignDetail | null;
  campaignDetailLoading: boolean;

  // Handlers - Campaigns
  fetchCampaignsData: () => Promise<{ success: boolean; error?: string }>;
  fetchCampaignDetail: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleCreateCampaign: (data: CampaignCreateRequest) => Promise<{ success: boolean; error?: string }>;
  handleStartCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handlePauseCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleResumeCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleCompleteCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleRestartCampaign: (campaignId: number, data: CampaignRestartRequest) => Promise<{ success: boolean; error?: string; contactsReset?: number }>;
  handleAddCampaignContacts: (campaignId: number, data: CampaignAddContactsRequest) => Promise<{ success: boolean; error?: string }>;
  handleRunCampaignById: (campaignId: number) => Promise<{ success: boolean; error?: string; result?: { sent: number; failed: number } }>;
  handleDeleteCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  clearSelectedCampaign: () => void;

  // Campaign messages state
  campaignMessages: HsmCampaignMessage[];
  messagesLoading: boolean;
  selectedMessage: HsmCampaignMessage | null;
  messageDialogOpen: boolean;
  setMessageDialogOpen: (open: boolean) => void;

  // Message form state
  messageDay: string;
  setMessageDay: (value: string) => void;
  messageTemplateName: string;
  setMessageTemplateName: (value: string) => void;
  messageLanguageCode: string;
  setMessageLanguageCode: (value: string) => void;
  messageTemplateParameters: string;
  setMessageTemplateParameters: (value: string) => void;
  messageVariableMapping: string[];
  setMessageVariableMapping: (value: string[]) => void;
  messageSaving: boolean;

  // Logs & stats state
  messageLogs: HsmMessageLog[];
  logsLoading: boolean;
  campaignStats: HsmCampaignStats | null;
  statsLoading: boolean;

  // Test message state
  testDialogOpen: boolean;
  setTestDialogOpen: (open: boolean) => void;
  testPhoneNumber: string;
  setTestPhoneNumber: (value: string) => void;
  testTemplateName: string;
  setTestTemplateName: (value: string) => void;
  testLanguageCode: string;
  setTestLanguageCode: (value: string) => void;
  testTemplateParameters: string;
  setTestTemplateParameters: (value: string) => void;
  testSending: boolean;

  // Permissions
  isAdmin: boolean;

  // Handlers - Contacts
  fetchContacts: () => Promise<{ success: boolean; error?: string }>;
  openNewContactDialog: () => void;
  openEditContactDialog: (contact: HsmContact) => void;
  handleCreateContact: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateContact: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDeleteContact: (contactId: number) => Promise<void>;
  handleActivateContact: (contactId: number) => Promise<void>;
  handleDeactivateContact: (contactId: number) => Promise<void>;

  // Handlers - Batches
  fetchBatches: () => Promise<{ success: boolean; error?: string }>;
  handleCreateBatch: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDeleteBatch: (batchId: number) => Promise<void>;
  openAssignBatchDialog: (contact: HsmContact) => void;
  handleAssignBatch: () => Promise<void>;
  openBatchEditDialog: (batch: HsmContactBatch) => void;
  handleUpdateBatch: (batchId: number, name: string, description?: string) => Promise<{ success: boolean; error?: string }>;
  handleCsvImport: (file: File, batchId?: number, batchName?: string) => Promise<{ success: boolean; error?: string; result?: import('../../types').HsmCsvImportResult }>;

  // Handlers - Campaign messages
  fetchCampaignMessages: () => Promise<{ success: boolean; error?: string }>;
  openNewMessageDialog: () => void;
  openEditMessageDialog: (message: HsmCampaignMessage) => void;
  handleCreateMessage: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateMessage: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleDeleteMessage: (messageId: number) => Promise<void>;

  // Handlers - Logs & stats
  fetchMessageLogs: () => Promise<{ success: boolean; error?: string }>;
  fetchCampaignStats: () => Promise<{ success: boolean; error?: string }>;

  // Handlers - Campaign execution
  handleRunCampaign: () => Promise<void>;
  handleRunCampaignDay: (day: number) => Promise<void>;

  // Handlers - Test message
  openTestDialog: () => void;
  handleSendTestMessage: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  sendTestMessage: (data: HsmTestMessageRequest) => Promise<{ success: boolean; error?: string }>;
  resetTestForm: () => void;

  // Templates state
  templates: HsmTemplate[];
  templatesLoading: boolean;
  templatesResponse: HsmTemplatesResponse | null;
  approvedTemplates: HsmTemplate[];
  templateCreating: boolean;

  // Handlers - Templates
  fetchTemplates: () => Promise<{ success: boolean; error?: string }>;
  handleCreateTemplate: (data: {
    name: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    bodyText: string;
    headerText?: string;
    headerType?: 'TEXT';
    footerText?: string;
    bodyExamples?: string[];
    headerExample?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  handleUpdateTemplate: (templateId: string, data: {
    bodyText: string;
    headerText?: string;
    headerType?: 'TEXT';
    footerText?: string;
    bodyExamples?: string[];
    headerExample?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  handleDeleteTemplate: (templateName: string) => Promise<{ success: boolean; error?: string }>;
  templateUpdating: boolean;
  templateDeleting: boolean;
  selectedTemplate: HsmTemplate | null;
  editTemplateDialogOpen: boolean;
  setEditTemplateDialogOpen: (open: boolean) => void;
  openEditTemplateDialog: (template: HsmTemplate) => void;
  closeEditTemplateDialog: () => void;

  // Template variables state
  templateVariables: TemplateVariable[];
  templateVariablesLoading: boolean;

  // Handlers - Template variables
  fetchTemplateVariables: () => Promise<{ success: boolean; error?: string }>;
  validateTemplateVariables: (templateBody?: string, variableMapping?: string[]) => Promise<{
    success: boolean;
    valid?: boolean;
    errors?: string[];
    detectedVariables?: string[];
    error?: string;
  }>;
  parseTemplateVariables: (templateBody: string) => Promise<{
    success: boolean;
    variables?: string[];
    placeholders?: string[];
    error?: string;
  }>;

  // Handlers - Template variable mappings
  saveTemplateVariableMapping: (templateName: string, variableMapping: string[]) => Promise<{ success: boolean; error?: string }>;
  getTemplateVariableMapping: (templateName: string) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
}

export function HsmPage({
  activeTab,
  setActiveTab,
  contacts,
  contactsLoading,
  contactsFilter,
  setContactsFilter,
  contactsSearch,
  setContactsSearch,
  contactsSort,
  toggleContactsSort,
  contactDialogOpen,
  setContactDialogOpen,
  bulkImportDialogOpen,
  setBulkImportDialogOpen,
  selectedContact,
  contactPhoneNumber,
  setContactPhoneNumber,
  contactName,
  setContactName,
  contactCompany,
  setContactCompany,
  contactCpfCnpj,
  setContactCpfCnpj,
  contactEmail,
  setContactEmail,
  contactCampaignDay,
  setContactCampaignDay,
  contactBatchId,
  setContactBatchId,
  contactSaving,
  batches,
  batchesLoading,
  selectedBatchFilter,
  setSelectedBatchFilter,
  batchDialogOpen,
  setBatchDialogOpen,
  batchName,
  setBatchName,
  batchDescription,
  setBatchDescription,
  batchSaving,
  assignBatchDialogOpen,
  setAssignBatchDialogOpen,
  contactToAssign,
  assignBatchId,
  setAssignBatchId,
  assigningBatch,
  batchEditDialogOpen,
  setBatchEditDialogOpen,
  batchToEdit,
  campaigns,
  campaignsLoading,
  selectedCampaign,
  campaignDetailLoading,
  fetchCampaignsData,
  fetchCampaignDetail,
  handleCreateCampaign,
  handleStartCampaign,
  handlePauseCampaign,
  handleResumeCampaign,
  handleCompleteCampaign,
  handleRestartCampaign,
  handleAddCampaignContacts,
  handleRunCampaignById,
  handleDeleteCampaign,
  clearSelectedCampaign,
  campaignMessages,
  messagesLoading,
  selectedMessage,
  messageDialogOpen,
  setMessageDialogOpen,
  messageDay,
  setMessageDay,
  messageTemplateName,
  setMessageTemplateName,
  messageLanguageCode,
  setMessageLanguageCode,
  messageTemplateParameters,
  setMessageTemplateParameters,
  messageVariableMapping,
  setMessageVariableMapping,
  messageSaving,
  messageLogs,
  logsLoading,
  campaignStats,
  statsLoading,
  testDialogOpen,
  setTestDialogOpen,
  testPhoneNumber,
  setTestPhoneNumber,
  testTemplateName,
  setTestTemplateName,
  testTemplateParameters,
  setTestTemplateParameters,
  testSending,
  isAdmin,
  fetchContacts,
  openNewContactDialog,
  openEditContactDialog,
  handleCreateContact,
  handleUpdateContact,
  handleDeleteContact,
  fetchBatches,
  handleCreateBatch,
  handleDeleteBatch,
  openAssignBatchDialog,
  handleAssignBatch,
  openBatchEditDialog,
  handleUpdateBatch,
  handleCsvImport,
  fetchCampaignMessages,
  openNewMessageDialog,
  openEditMessageDialog,
  handleCreateMessage,
  handleUpdateMessage,
  handleDeleteMessage,
  fetchMessageLogs,
  fetchCampaignStats,
  handleRunCampaign,
  openTestDialog,
  sendTestMessage,
  resetTestForm,
  templates,
  templatesLoading,
  templatesResponse,
  approvedTemplates,
  templateCreating,
  fetchTemplates,
  handleCreateTemplate,
  handleUpdateTemplate,
  handleDeleteTemplate,
  templateUpdating,
  templateDeleting,
  selectedTemplate,
  editTemplateDialogOpen,
  setEditTemplateDialogOpen,
  openEditTemplateDialog,
  closeEditTemplateDialog,
  templateVariables,
  templateVariablesLoading,
  fetchTemplateVariables,
  validateTemplateVariables,
  saveTemplateVariableMapping,
  getTemplateVariableMapping
}: HsmPageProps) {
  // Fetch initial data when component mounts
  useEffect(() => {
    fetchContacts();
    fetchBatches();
    fetchCampaignMessages();
    fetchTemplates();
    fetchCampaignsData();
  }, [fetchContacts, fetchBatches, fetchCampaignMessages, fetchTemplates, fetchCampaignsData]);

  // Fetch logs and stats when logs tab is selected
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchMessageLogs();
      fetchCampaignStats();
    }
  }, [activeTab, fetchMessageLogs, fetchCampaignStats]);

  // Handle test message submit with proper type conversion
  const handleTestMessageSubmit = useCallback(
    async (data: HsmTestMessageRequest) => {
      return sendTestMessage(data);
    },
    [sendTestMessage]
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Gerenciador de Campanhas HSM
              </CardTitle>
              <CardDescription>
                Gerencie contatos e campanhas de WhatsApp HSM
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <Button variant="outline" onClick={openTestDialog}>
                  <Send className="mr-2 h-4 w-4" />
                  Teste
                </Button>
              )}
              <Button onClick={handleRunCampaign}>
                <Play className="mr-2 h-4 w-4" />
                Executar Campanha
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as HsmActiveTab)}>
            <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
              <TabsTrigger value="contacts" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Contatos</span>
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">Campanhas</span>
              </TabsTrigger>
              <TabsTrigger value="logs" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Logs</span>
              </TabsTrigger>
              <TabsTrigger value="templates" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden sm:inline">Templates</span>
              </TabsTrigger>
            </TabsList>

            {/* Contacts Tab */}
            <TabsContent value="contacts">
              <HsmContactsTab
                contacts={contacts}
                contactsLoading={contactsLoading}
                contactsFilter={contactsFilter}
                setContactsFilter={setContactsFilter}
                contactsSearch={contactsSearch}
                setContactsSearch={setContactsSearch}
                contactsSort={contactsSort}
                toggleContactsSort={toggleContactsSort}
                contactDialogOpen={contactDialogOpen}
                setContactDialogOpen={setContactDialogOpen}
                bulkImportDialogOpen={bulkImportDialogOpen}
                setBulkImportDialogOpen={setBulkImportDialogOpen}
                selectedContact={selectedContact}
                contactPhoneNumber={contactPhoneNumber}
                setContactPhoneNumber={setContactPhoneNumber}
                contactName={contactName}
                setContactName={setContactName}
                contactCompany={contactCompany}
                setContactCompany={setContactCompany}
                contactCpfCnpj={contactCpfCnpj}
                setContactCpfCnpj={setContactCpfCnpj}
                contactEmail={contactEmail}
                setContactEmail={setContactEmail}
                contactCampaignDay={contactCampaignDay}
                setContactCampaignDay={setContactCampaignDay}
                contactBatchId={contactBatchId}
                setContactBatchId={setContactBatchId}
                contactSaving={contactSaving}
                batches={batches}
                batchesLoading={batchesLoading}
                selectedBatchFilter={selectedBatchFilter}
                setSelectedBatchFilter={setSelectedBatchFilter}
                batchDialogOpen={batchDialogOpen}
                setBatchDialogOpen={setBatchDialogOpen}
                batchName={batchName}
                setBatchName={setBatchName}
                batchDescription={batchDescription}
                setBatchDescription={setBatchDescription}
                batchSaving={batchSaving}
                assignBatchDialogOpen={assignBatchDialogOpen}
                setAssignBatchDialogOpen={setAssignBatchDialogOpen}
                contactToAssign={contactToAssign}
                assignBatchId={assignBatchId}
                setAssignBatchId={setAssignBatchId}
                assigningBatch={assigningBatch}
                batchEditDialogOpen={batchEditDialogOpen}
                setBatchEditDialogOpen={setBatchEditDialogOpen}
                batchToEdit={batchToEdit}
                isAdmin={isAdmin}
                fetchContacts={fetchContacts}
                fetchBatches={fetchBatches}
                openNewContactDialog={openNewContactDialog}
                openEditContactDialog={openEditContactDialog}
                handleCreateContact={handleCreateContact}
                handleUpdateContact={handleUpdateContact}
                handleDeleteContact={handleDeleteContact}
                handleCreateBatch={handleCreateBatch}
                handleDeleteBatch={handleDeleteBatch}
                openAssignBatchDialog={openAssignBatchDialog}
                handleAssignBatch={handleAssignBatch}
                openBatchEditDialog={openBatchEditDialog}
                handleUpdateBatch={handleUpdateBatch}
                handleCsvImport={handleCsvImport}
              />
            </TabsContent>

            {/* Campaigns Tab */}
            <TabsContent value="campaigns">
              <HsmCampaignsTab
                campaigns={campaigns}
                campaignsLoading={campaignsLoading}
                selectedCampaign={selectedCampaign}
                campaignDetailLoading={campaignDetailLoading}
                approvedTemplates={approvedTemplates}
                templatesLoading={templatesLoading}
                contacts={contacts}
                contactsLoading={contactsLoading}
                batches={batches}
                batchesLoading={batchesLoading}
                isAdmin={isAdmin}
                fetchCampaigns={fetchCampaignsData}
                fetchCampaignDetail={fetchCampaignDetail}
                handleCreateCampaign={handleCreateCampaign}
                handleStartCampaign={handleStartCampaign}
                handlePauseCampaign={handlePauseCampaign}
                handleResumeCampaign={handleResumeCampaign}
                handleCompleteCampaign={handleCompleteCampaign}
                handleRestartCampaign={handleRestartCampaign}
                handleAddContacts={handleAddCampaignContacts}
                handleRunCampaign={handleRunCampaignById}
                handleDeleteCampaign={handleDeleteCampaign}
                clearSelectedCampaign={clearSelectedCampaign}
                getTemplateVariableMapping={getTemplateVariableMapping}
              />
            </TabsContent>

            {/* Logs Tab */}
            <TabsContent value="logs">
              <HsmLogsTab
                messageLogs={messageLogs}
                logsLoading={logsLoading}
                campaignStats={campaignStats}
                statsLoading={statsLoading}
                fetchMessageLogs={fetchMessageLogs}
                fetchCampaignStats={fetchCampaignStats}
              />
            </TabsContent>

            {/* Templates Tab */}
            <TabsContent value="templates">
              <HsmTemplatesTab
                templates={templates}
                templatesLoading={templatesLoading}
                templatesResponse={templatesResponse}
                templateCreating={templateCreating}
                templateUpdating={templateUpdating}
                selectedTemplate={selectedTemplate}
                editTemplateDialogOpen={editTemplateDialogOpen}
                setEditTemplateDialogOpen={setEditTemplateDialogOpen}
                fetchTemplates={fetchTemplates}
                handleCreateTemplate={handleCreateTemplate}
                handleUpdateTemplate={handleUpdateTemplate}
                openEditTemplateDialog={openEditTemplateDialog}
                closeEditTemplateDialog={closeEditTemplateDialog}
                saveTemplateVariableMapping={saveTemplateVariableMapping}
                getTemplateVariableMapping={getTemplateVariableMapping}
                handleDeleteTemplate={handleDeleteTemplate}
                templateDeleting={templateDeleting}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Test Message Dialog */}
      <HsmTestMessageDialog
        open={testDialogOpen}
        onOpenChange={setTestDialogOpen}
        phoneNumber={testPhoneNumber}
        setPhoneNumber={setTestPhoneNumber}
        templateName={testTemplateName}
        setTemplateName={setTestTemplateName}
        templateParameters={testTemplateParameters}
        setTemplateParameters={setTestTemplateParameters}
        sending={testSending}
        approvedTemplates={approvedTemplates}
        templatesLoading={templatesLoading}
        onSubmit={handleTestMessageSubmit}
        resetForm={resetTestForm}
      />
    </>
  );
}
